import React, { useRef, useState } from 'react';
import { AlertCircleIcon, CheckCircle2Icon, Loader2Icon, PaperclipIcon, SendIcon, XIcon } from 'lucide-react';
import { FUNDING_RANGES, PURPOSES, INDUSTRIES } from '../../data/content';
import { useLanguage } from '../../i18n/LanguageContext';
import { COMPANY } from '../../data/company';
import { FORM_ENDPOINT, NOTIFICATION_EMAIL, WEB3FORMS_ACCESS_KEY, isFormConfigured } from '../../data/formConfig';
type FormState = {
  name: string;
  company: string;
  country: string;
  email: string;
  telephone: string;
  funding: string;
  purpose: string;
  industry: string;
  message: string;
};
const EMPTY: FormState = {
  name: '',
  company: '',
  country: '',
  email: '',
  telephone: '',
  funding: '',
  purpose: '',
  industry: '',
  message: ''
};
type Status = 'idle' | 'submitting' | 'success' | 'invalid' | 'error';
const inputClass = 'w-full rounded-xl border border-navy-100 bg-white px-4 py-3 text-sm text-charcoal placeholder:text-charcoal-light/50 transition-colors focus:border-gold focus:outline-none';
const labelClass = 'block text-[11px] font-semibold uppercase tracking-[0.14em] text-navy';
export function EnquiryForm() {
  const {
    lang,
    p
  } = useLanguage();
  const f = p.contact.form;
  const [form, setForm] = useState<FormState>(EMPTY);
  const [files, setFiles] = useState<File[]>([]);
  const [status, setStatus] = useState<Status>('idle');
  const [serverMessage, setServerMessage] = useState<string | null>(null);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const fileInput = useRef<HTMLInputElement>(null);
  const update = (key: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm(prev => ({
      ...prev,
      [key]: e.target.value
    }));
    setErrors(prev => ({
      ...prev,
      [key]: undefined
    }));
  };
  const validate = () => {
    const next: Partial<Record<keyof FormState, string>> = {};
    if (!form.name.trim()) next.name = f.errors.name;
    if (!form.country.trim()) next.country = f.errors.country;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) next.email = f.errors.email;
    if (!form.funding) next.funding = f.errors.funding;
    if (!form.message.trim() || form.message.trim().length < 20) next.message = f.errors.message;
    setErrors(next);
    return Object.keys(next).length === 0;
  };
  const composedMessage = () => [`Nombre / Name: ${form.name}`, `Empresa / Company: ${form.company || '—'}`, `País / Country: ${form.country}`, `Email: ${form.email}`, `Teléfono / Telephone: ${form.telephone || '—'}`, `Financiación / Funding required: ${form.funding}`, `Finalidad / Purpose: ${form.purpose || '—'}`, `Sector / Industry: ${form.industry || '—'}`, `Documentos / Documents: ${files.length ? files.map(file => file.name).join(', ') : '—'}`, `Idioma / Language: ${lang === 'es' ? 'Español' : 'English'}`, '', 'Mensaje / Message:', form.message].join('\n');
  const buildPayload = () => ({
    access_key: WEB3FORMS_ACCESS_KEY,
    subject: `Consulta de financiación — ${form.name}${form.company ? ` (${form.company})` : ''}`,
    from_name: `${COMPANY.shortName} · Web`,
    name: form.name,
    email: form.email,
    replyto: form.email,
    phone: form.telephone || '—',
    company: form.company || '—',
    country: form.country,
    funding_required: form.funding,
    purpose: form.purpose || '—',
    industry: form.industry || '—',
    documents: files.length ? files.map(file => file.name).join(', ') : '—',
    message: composedMessage()
  });
  const mailtoFallback = () => `mailto:${NOTIFICATION_EMAIL}?subject=${encodeURIComponent(`Consulta de financiación — ${form.name}`)}&body=${encodeURIComponent(composedMessage())}`;
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) {
      setStatus('invalid');
      return;
    }
    if (!isFormConfigured()) {
      setServerMessage('Missing Web3Forms access key (see data/formConfig.ts).');
      setStatus('error');
      return;
    }
    setStatus('submitting');
    setServerMessage(null);
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(buildPayload()),
        signal: controller.signal
      });
      const result = await response.json().catch(() => null);
      // Kept intentionally: surfaces the provider's exact response while the form is being verified.
      console.info('[enquiry-form] Web3Forms response', response.status, result);
      if (!response.ok || !result || result.success !== true) {
        setServerMessage(result?.message ? `${response.status} · ${result.message}` : `HTTP ${response.status}`);
        setStatus('error');
        return;
      }
      setStatus('success');
      setForm(EMPTY);
      setFiles([]);
    } catch (err) {
      console.error('[enquiry-form] send failed', err);
      setServerMessage(err instanceof Error ? err.message : 'Network error');
      setStatus('error');
    } finally {
      window.clearTimeout(timeout);
    }
  };
  const handleFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const list = Array.from(e.target.files ?? []);
    setFiles(prev => [...prev, ...list].slice(0, 5));
    e.target.value = '';
  };
  if (status === 'success') {
    return <div role="status" className="rounded-3xl border border-navy-100 bg-white p-10 text-center shadow-soft" data-id="element-21" data-mp="2w3zr3qfxaar9">
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-navy text-gold" data-id="element-22" data-mp="306362p1svxha">
          <CheckCircle2Icon className="h-6 w-6" aria-hidden="true" data-id="element-23" data-mp="ycwtqiheb1c6" />
        </span>
        <h3 className="mt-6 font-display text-2xl font-medium text-navy" data-id="element-24" data-mp="yasqm2rbm6cy">{f.successTitle}</h3>
        <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-charcoal-light" data-id="element-25" data-mp="277r0yzhia5sl">
          {f.successText}
        </p>
        <button type="button" onClick={() => setStatus('idle')} className="mt-8 inline-flex items-center gap-2 rounded-full border border-navy-100 px-6 py-3 text-[12px] font-semibold uppercase tracking-[0.16em] text-navy transition-colors hover:border-gold hover:text-gold-dark" data-id="element-26" data-mp="1y8vsthuwilmn">
          {f.successAgain}
        </button>
      </div>;
  }
  return <form onSubmit={handleSubmit} noValidate className="rounded-3xl border border-navy-100 bg-white p-7 shadow-soft sm:p-10" data-id="element-27" data-mp="2h81b2b88ux3j">
      <h2 className="font-display text-2xl font-medium text-navy sm:text-3xl" data-id="element-28" data-mp="2c855w8nt8zme">{f.title}</h2>
      <p className="mt-3 text-sm leading-relaxed text-charcoal-light" data-id="element-29" data-mp="39p40fxp6wn56">{f.intro}</p>

      <div className="mt-8 grid gap-6 sm:grid-cols-2" data-id="element-30" data-mp="34xc416jcrn74">
        <div data-id="element-31" data-mp="34vra9j4v48ab">
          <label htmlFor="name" className={labelClass} data-id="element-32" data-mp="3c90ba8xgpqbt">
            {f.name}
          </label>
          <input id="name" name="name" autoComplete="name" className={`${inputClass} mt-2`} value={form.name} onChange={update('name')} aria-invalid={!!errors.name} placeholder={f.namePlaceholder} data-id="element-33" data-mp="3h7tsocfve1h9" />
          {errors.name && <FieldError message={errors.name} data-id="element-34" data-mp="3v84f93a5ctwm" />}
        </div>

        <div data-id="element-35" data-mp="3v5s356r2ldrh">
          <label htmlFor="company" className={labelClass} data-id="element-36" data-mp="41bhyaih5exd">
            {f.company}
          </label>
          <input id="company" name="organization" autoComplete="organization" className={`${inputClass} mt-2`} value={form.company} onChange={update('company')} placeholder={f.companyPlaceholder} data-id="element-37" data-mp="8s1qfwm75xwa" />
        </div>

        <div data-id="element-38" data-mp="mrjg0e7bhf6p">
          <label htmlFor="country" className={labelClass} data-id="element-39" data-mp="mr1ccwibryor">
            {f.country}
          </label>
          <input id="country" name="country" autoComplete="country-name" className={`${inputClass} mt-2`} value={form.country} onChange={update('country')} aria-invalid={!!errors.country} placeholder={f.countryPlaceholder} data-id="element-40" data-mp="181yiuz7t1rb1" />
          {errors.country && <FieldError message={errors.country} data-id="element-41" data-mp="1czrkrg22rz5i" />}
        </div>

        <div data-id="element-42" data-mp="pdbao7uc0lt8">
          <label htmlFor="email" className={labelClass} data-id="element-43" data-mp="pcrt4s8lfzfq">
            {f.email}
          </label>
          <input id="email" name="email" type="email" autoComplete="email" className={`${inputClass} mt-2`} value={form.email} onChange={update('email')} aria-invalid={!!errors.email} placeholder={f.emailPlaceholder} data-id="element-44" data-mp="keqcedyn4c7f" />
          {errors.email && <FieldError message={errors.email} data-id="element-45" data-mp="1mxmisevb73x" />}
        </div>

        <div data-id="element-46" data-mp="1kl4kf940wqv">
          <label htmlFor="telephone" className={labelClass} data-id="element-47" data-mp="1kuszyeiavug">
            {f.phone}
          </label>
          <input id="telephone" name="tel" type="tel" autoComplete="tel" className={`${inputClass} mt-2`} value={form.telephone} onChange={update('telephone')} placeholder={f.phonePlaceholder} data-id="element-48" data-mp="2d87w6i98v5kk" />
        </div>

        <div data-id="element-49" data-mp="2w0s8ao0q4w1o">
          <label htmlFor="funding" className={labelClass} data-id="element-50" data-mp="2w0903z0vlm0u">
            {f.funding}
          </label>
          <select id="funding" className={`${inputClass} mt-2`} value={form.funding} onChange={update('funding')} aria-invalid={!!errors.funding} data-id="element-51" data-mp="30zvdc8gpf29n">
            <option value="" data-id="element-52" data-mp="2r04ho3geqh7z">{f.fundingPlaceholder}</option>
            {FUNDING_RANGES.map(range => <option key={range.en} value={range[lang]} data-id="element-53" data-mp="1bj41yxozggur">
                {range[lang]}
              </option>)}
          </select>
          {errors.funding && <FieldError message={errors.funding} data-id="element-54" data-mp="3t4by4hmm90c" />}
        </div>

        <div data-id="element-55" data-mp="3vpd0ra3okx1">
          <label htmlFor="purpose" className={labelClass} data-id="element-56" data-mp="8spk95iortpq">
            {f.purpose}
          </label>
          <select id="purpose" className={`${inputClass} mt-2`} value={form.purpose} onChange={update('purpose')} data-id="element-57" data-mp="3r6oqz4pgomvt">
            <option value="" data-id="element-58" data-mp="3v67ca1l72zz6">{f.purposePlaceholder}</option>
            {PURPOSES.map(purpose => <option key={purpose.en} value={purpose[lang]} data-id="element-59" data-mp="3halry7pwp4d3">
                {purpose[lang]}
              </option>)}
          </select>
        </div>

        <div data-id="element-60" data-mp="3895rarow8gm2">
          <label htmlFor="industry" className={labelClass} data-id="element-61" data-mp="389f7xoo1tbf3">
            {f.industry}
          </label>
          <select id="industry" className={`${inputClass} mt-2`} value={form.industry} onChange={update('industry')} data-id="element-62" data-mp="3c9pofv9gvk87">
            <option value="" data-id="element-63" data-mp="1agrprit0dxfc">{f.industryPlaceholder}</option>
            {INDUSTRIES.map(industry => <option key={industry.name.en} value={industry.name[lang]} data-id="element-64" data-mp="2v0wrij2az3hy">
                {industry.name[lang]}
              </option>)}
            <option value={f.other} data-id="element-65" data-mp="34pwyaon1r36p">{f.other}</option>
          </select>
        </div>

        <div className="sm:col-span-2" data-id="element-66" data-mp="34rrjm428cxbb">
          <label htmlFor="message" className={labelClass} data-id="element-67" data-mp="2zyya72vth0sw">
            {f.message}
          </label>
          <textarea id="message" rows={5} className={`${inputClass} mt-2 resize-y`} value={form.message} onChange={update('message')} aria-invalid={!!errors.message} placeholder={f.messagePlaceholder} data-id="element-68" data-mp="3enb8w7magxr3" />
          {errors.message && <FieldError message={errors.message} data-id="element-69" data-mp="3iqzj6y2b27g1" />}
        </div>

        <div className="sm:col-span-2" data-id="element-70" data-mp="1ta87fm6o9k0m">
          <span className={labelClass} data-id="element-71" data-mp="1tb9uyq4vjyz8">{f.upload}</span>
          <button type="button" onClick={() => fileInput.current?.click()} className="mt-2 flex w-full items-center justify-center gap-3 rounded-xl border border-dashed border-navy-100 bg-mist px-4 py-6 text-center text-sm text-charcoal-light transition-colors hover:border-gold hover:text-navy" data-id="element-72" data-mp="1oc6pj4kzfqqq">
            <PaperclipIcon className="h-4 w-4 shrink-0" aria-hidden="true" data-id="element-73" data-mp="255270cabj6a" />
            {f.uploadHint}
          </button>
          <input ref={fileInput} id="documents" type="file" multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx" onChange={handleFiles} className="sr-only" data-id="element-74" data-mp="66745oxl69by" />
          {files.length > 0 && <ul className="mt-3 flex flex-wrap gap-2" data-id="element-75" data-mp="kzakxu3csj1m">
              {files.map((file, idx) => <li key={`${file.name}-${idx}`} className="flex items-center gap-2 rounded-full bg-navy-50 px-4 py-2 text-xs text-navy" data-id="element-76" data-mp="pydeoidvv2ua" data-mpi={idx}>
                  {file.name}
                  <button type="button" aria-label={`${f.remove} ${file.name}`} onClick={() => setFiles(prev => prev.filter((_, j) => j !== idx))} className="text-charcoal-light transition-colors hover:text-gold-dark" data-id="element-77" data-mp="2jezvuy2kqsyg">
                    <XIcon className="h-3 w-3" aria-hidden="true" data-id="element-78" data-mp="25frtjwb474q1" />
                  </button>
                </li>)}
            </ul>}
        </div>
      </div>

      {(status === 'invalid' || status === 'error') && <div role="alert" className="mt-6 flex items-start gap-2 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700" data-id="element-79" data-mp="2y4fl29mgiqc1">
          <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-80" data-mp="2oecfzb229lxc" />
          {status === 'invalid' ? <p data-id="element-81" data-mp="3lujoogznaq31">{f.errorBanner}</p> : <p data-id="element-82" data-mp="3lvb9cwrpf3ej">
              {f.sendError}{' '}
              <a href={mailtoFallback()} className="font-semibold underline underline-offset-2" data-id="element-83" data-mp="3h1qj5qwcrdpw">
                {COMPANY.email}
              </a>
              {serverMessage && <span className="mt-1 block text-xs text-red-600/80" data-id="element-84" data-mp="35mvd901nz0yd">{serverMessage}</span>}
            </p>}
        </div>}

      <button type="submit" disabled={status === 'submitting'} className="group mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-navy px-8 py-4 text-[12px] font-semibold uppercase tracking-[0.16em] text-white transition-all duration-300 hover:bg-navy-700 hover:shadow-lift disabled:opacity-60 sm:w-auto" data-id="element-85" data-mp="2ronj0zw0idqw">
        {status === 'submitting' ? <>
            <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-86" data-mp="3ajlzk542ass8" />
            {f.submitting}
          </> : <>
            {f.submit}
            <SendIcon className="h-4 w-4 text-gold transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" data-id="element-87" data-mp="7353y5h1keb4" />
          </>}
      </button>

      <p className="mt-5 text-xs leading-relaxed text-charcoal-light" data-id="element-88" data-mp="l8f6jow89468">{f.consent}</p>
    </form>;
}
function FieldError({
  message
}: {
  message: string;
}) {
  return <p className="mt-2 flex items-center gap-1.5 text-xs text-red-600" data-id="element-89" data-mp="uz089l776otd">
      <AlertCircleIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-90" data-mp="q0gif9phgsvq" />
      {message}
    </p>;
}